import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { AdSlot } from "@/components/AdSlot";
import { track } from "@/components/layernav/navConfig";
import { PolicyHero } from "@/components/sector/PolicyHero";
import { RegulationRadarBand } from "@/components/sector/RegulationRadar";
import { JurisdictionStatus } from "@/components/sector/JurisdictionStatus";
import { PolicyFrontLayer } from "@/components/sector/PolicyFrontLayer";
import { PolicyIntelligence } from "@/components/sector/PolicyIntelligence";
import { JurisdictionIntelligence } from "@/components/sector/JurisdictionIntelligence";
import { RegulationTrackerModule } from "@/components/sector/RegulationTrackerModule";
import { PolicyImpact } from "@/components/sector/PolicyImpact";
import { EnforcementWatch, LegislationRulemaking } from "@/components/sector/PolicyLowerModules";
import { LatestCrypto, OriginalsSection, ResearchSection, ExplainersRail } from "@/components/sector/SectorSections";
import { SectorClose } from "@/components/sector/SectorClose";

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "NewsLayer", item: "https://newslayer.com/" },
    { "@type": "ListItem", position: 2, name: "Policy", item: "https://newslayer.com/policy" },
  ],
};

export default function PolicySectorPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["sector-policy"],
    queryFn: async () => (await api.get("/sectors/policy/overview")).data,
    refetchInterval: 120000,
    staleTime: 60000,
  });
  const { data: lower } = useQuery({
    queryKey: ["sector-policy-lower"],
    queryFn: async () => (await api.get("/sectors/policy/lower")).data,
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    track("sector_page_view", { sector: "policy" });
  }, []);

  return (
    <div data-testid="policy-sector-page" className="pb-16">
      <Seo title="Crypto Policy, Regulation & Jurisdiction Intelligence — NewsLayer"
        description="Regulation, jurisdictions, enforcement and the policy forces shaping the onchain economy — tracked frameworks, validated policy coverage and NewsLayer's regulation link graph."
        path="/policy" jsonLd={BREADCRUMB_LD} />
      {/* First view (APPROVED/FROZEN): Masthead+Pulse → Regulation Radar → Jurisdictions → Front Layer */}
      <PolicyHero data={data} />
      <RegulationRadarBand activity={data?.regulatory_activity || []} />
      <JurisdictionStatus items={data?.jurisdictions || []} />
      {isLoading ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8" aria-hidden>
          <div className="h-[340px] rounded-xl border border-white/[0.06] bg-white/[0.02] animate-pulse" />
        </div>
      ) : (
        <PolicyFrontLayer lead={data?.lead || null} secondary={data?.secondary || []} />
      )}

      {/* Lower page (§7 order) — every module hides adaptively when its data is absent */}
      <PolicyIntelligence data={lower?.intelligence} />
      <JurisdictionIntelligence items={lower?.jurisdictions || []} />
      <RegulationTrackerModule items={lower?.tracker || []} />
      <PolicyImpact items={lower?.impact || []} note={lower?.impact_note} />
      <LegislationRulemaking data={lower?.legislation} />
      <EnforcementWatch items={lower?.enforcement || []} courts={lower?.courts || []} />
      <LatestCrypto items={lower?.latest || []} title="Latest Policy" testid="latest-policy" sector="policy" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
        <AdSlot placement="sector_mid" />
      </div>
      <OriginalsSection items={lower?.originals || []} title="Policy Originals" testid="policy-originals" sector="policy" />
      <ResearchSection items={lower?.research || []} testid="policy-research" sector="policy" />
      <ExplainersRail items={lower?.explainers || []} title="Policy, Explained" testid="policy-explainers" sector="policy" />
      <SectorClose sector="policy" label="Policy" />
    </div>
  );
}
